import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, TextField, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';

const formatDatetimeLocal = (ts) => {
  if (!ts) return '';
  try {
    const date = ts?.toDate ? ts.toDate()
      : typeof ts === 'number' ? new Date(ts)
      : new Date(ts);
    if (isNaN(date.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  } catch { return ''; }
};

const getMinDateTime = () => formatDatetimeLocal(Date.now());

export const DeliveryTimeForm = ({
  earliestTargetTime: initialEarliestTargetTime,
  setEarliestTargetTime,
  targetTime: initialTargetTime,
  setTargetTime,
  hasTimeWindow,
  setHasTimeWindow,
  pickupTime, // read-only, for min constraint on target
}) => {
  const { t } = useTranslation();

  const [earliestTargetTime, setLocalEarliestTargetTime] = useState(initialEarliestTargetTime ?? null);
  const [targetTime,         setLocalTargetTime]         = useState(initialTargetTime         ?? null);

  useEffect(() => { setLocalEarliestTargetTime(initialEarliestTargetTime ?? null); }, [initialEarliestTargetTime]);
  useEffect(() => { setLocalTargetTime(initialTargetTime                 ?? null); }, [initialTargetTime]);

  const handleEarliestTargetChange = useCallback((e) => {
    const ms = new Date(e.target.value).getTime();
    setLocalEarliestTargetTime(ms);
    setEarliestTargetTime?.(ms);
  }, [setEarliestTargetTime]);

  const handleTargetChange = useCallback((e) => {
    const ms = new Date(e.target.value).getTime();
    setLocalTargetTime(ms);
    setTargetTime?.(ms);
  }, [setTargetTime]);

  const toggleTimeWindow = useCallback(() => {
    const next = !hasTimeWindow;
    setHasTimeWindow?.(next);
    if (next && targetTime && !earliestTargetTime) {
      // default earliest target = target - 1h
      const ms = targetTime - 60 * 60000;
      setLocalEarliestTargetTime(ms);
      setEarliestTargetTime?.(ms);
    }
  }, [hasTimeWindow, setHasTimeWindow, targetTime, earliestTargetTime, setEarliestTargetTime]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>

        {/* Earliest target — only when hasTimeWindow */}
        {hasTimeWindow && (
          <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: 200 }}>
            <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
              {t('delivery.earliestDelivery') || 'Earliest Delivery'}
            </Typography>
            <TextField
              fullWidth size="small" type="datetime-local"
              value={formatDatetimeLocal(earliestTargetTime)}
              onChange={handleEarliestTargetChange}
              InputLabelProps={{ shrink: true }}
              inputProps={{
                min: pickupTime ? formatDatetimeLocal(pickupTime) : getMinDateTime(),
                max: targetTime ? formatDatetimeLocal(targetTime) : undefined,
              }}
            />
          </Box>
        )}

        {/* Latest target — always shown */}
        <Box sx={{ flex: hasTimeWindow ? '1 1 calc(50% - 8px)' : '1 1 100%', minWidth: 200 }}>
          <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>

            {(t('delivery.targetTime') || 'Target Delivery Time')}
          </Typography>
          <TextField
            fullWidth size="small" type="datetime-local"
            value={formatDatetimeLocal(targetTime)}
            onChange={handleTargetChange}
            InputLabelProps={{ shrink: true }}
            inputProps={{
              min: earliestTargetTime
                ? formatDatetimeLocal(earliestTargetTime)
                : pickupTime ? formatDatetimeLocal(pickupTime) : getMinDateTime(),
            }}
          />
        </Box>
      </Box>

      {/* Toggle — no alert, already shown in PickupTimeForm */}
      <Box>
        <Button
          variant={hasTimeWindow ? 'contained' : 'outlined'}
          onClick={toggleTimeWindow}
          size="small"
        >
          {hasTimeWindow
            ? (t('delivery.hideTimeWindow') || 'Hide Time Window')
            : (t('delivery.setTimeWindow')  || 'Set Time Window')}
        </Button>
      </Box>
    </Box>
  );
};